import { formatNumber } from 'cesg-shared';
import type { SessionUser } from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLiftPlanUEPermissions } from '../permissions/useLiftPlanUEPermissions';
import type { LiftPlanComponentState } from '../types';
import type {
  LiftPlanAtaColumnKey,
  LiftPlanAtaRow,
  LiftPlanEntry,
  LiftPlanEntryColumnKey,
  LiftPlanSubtotalColumnKey,
  LiftPlanSubtotalRow,
} from './createLiftPlanUEStore';
import {
  ALL_COLUMN_KEYS,
  createLiftPlanUEStore,
} from './createLiftPlanUEStore';

// --- Internal types ---

type LiftPlanInputState = 'edit' | 'read-only' | 'hidden';

interface LiftPlanColumn {
  key: LiftPlanEntryColumnKey;
  label: string;
  state: LiftPlanInputState;
  getValue: (entry: LiftPlanEntry) => string;
}

interface LiftPlanSubtotalColumn {
  key: LiftPlanSubtotalColumnKey;
  state: LiftPlanInputState;
  getValue: (row: LiftPlanSubtotalRow) => string;
}

interface LiftPlanAtaColumn {
  key: LiftPlanAtaColumnKey;
  state: LiftPlanInputState;
  getValue: (row: LiftPlanAtaRow) => string;
}

export type { LiftPlan } from './createLiftPlanUEStore';

// --- Save payload type ---

export type SaveLiftPlanUEData = {
  name: string;
  contractSum: number;
  retentionPercentage: number;
  entries: LiftPlanEntry[];
  subtotalRows: LiftPlanSubtotalRow[];
  ataRows: LiftPlanAtaRow[];
};

// --- Emit type ---

export type UseLiftPlanUEEmit = {
  (event: 'delete'): void;
  (event: 'save', data: SaveLiftPlanUEData): void;
};

// --- Props ---

export interface UseLiftPlanUELogicProps {
  initialLiftPlan: Parameters<typeof createLiftPlanUEStore>[0];
  user: SessionUser;
  projectId: string;
  isSaving?: boolean;
}

const GET_VALUE: Record<
  LiftPlanEntryColumnKey,
  (entry: LiftPlanEntry) => string
> = {
  number: (entry) => entry.number.toString(),
  description: (entry) => entry.description ?? '',
  dueDate: (entry) =>
    entry.dueDate ? entry.dueDate.toISOString().slice(0, 10) : '',
  amount: (entry) => formatNumber(entry.amount),
  retention: (entry) => formatNumber(entry.retention),
  amountDue: (entry) => formatNumber(entry.amountDue),
  invoicedDate: (entry) =>
    entry.invoicedDate ? entry.invoicedDate.toISOString().slice(0, 10) : '',
  paymentDate: (entry) =>
    entry.paymentDate ? entry.paymentDate.toISOString().slice(0, 10) : '',
  comment: (entry) => entry.comment ?? '',
};

const SUBTOTAL_GET_VALUE: Record<
  LiftPlanSubtotalColumnKey,
  (row: LiftPlanSubtotalRow) => string
> = {
  label: (row) => row.label,
  amount: (row) => formatNumber(row.amount),
  invoicedDate: (row) =>
    row.invoicedDate ? row.invoicedDate.toISOString().slice(0, 10) : '',
  paymentDate: (row) =>
    row.paymentDate ? row.paymentDate.toISOString().slice(0, 10) : '',
  comment: (row) => row.comment ?? '',
};

const ATA_GET_VALUE: Record<
  LiftPlanAtaColumnKey,
  (row: LiftPlanAtaRow) => string
> = {
  label: (row) => row.label,
  amount: (row) => formatNumber(row.amount),
  invoicedDate: (row) =>
    row.invoicedDate ? row.invoicedDate.toISOString().slice(0, 10) : '',
  paymentDate: (row) =>
    row.paymentDate ? row.paymentDate.toISOString().slice(0, 10) : '',
  comment: (row) => row.comment ?? '',
};

// --- Utility ---

const createEventHandler = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Events extends Record<string, (...args: any[]) => void>,
>(
  events: Events,
) => events;

export const useLiftPlanUELogic = (
  props: UseLiftPlanUELogicProps,
  emit: UseLiftPlanUEEmit,
) => {
  const { initialLiftPlan, user, projectId } = props;
  const { t } = useI18n();

  const { state: storeState, dispatch } =
    createLiftPlanUEStore(initialLiftPlan);

  const mode = ref<'normal' | 'edit' | 'saving'>('normal');

  watch(
    () => props.isSaving,
    (saving) => {
      if (!saving && mode.value === 'saving') {
        mode.value = 'normal';
      }
    },
  );

  const permissions = useLiftPlanUEPermissions({
    isEditing: computed(() => storeState.value.isEditing),
    user,
    projectId,
  });

  // Computed state

  const columns = computed<
    Partial<Record<LiftPlanEntryColumnKey, LiftPlanColumn>>
  >(() => {
    const result: Partial<Record<LiftPlanEntryColumnKey, LiftPlanColumn>> = {};
    for (const key of ALL_COLUMN_KEYS) {
      if (storeState.value.columnStates[key] !== 'hidden') {
        result[key] = {
          key,
          label: t(`projects.liftPlanUE.columns.${key}`),
          state: permissions.value.columns[key],
          getValue: GET_VALUE[key],
        };
      }
    }
    return result;
  });

  const subtotalColumns = computed<
    Partial<Record<LiftPlanSubtotalColumnKey, LiftPlanSubtotalColumn>>
  >(() => ({
    label: {
      key: 'label',
      state: permissions.value.subtotalColumns.label,
      getValue: SUBTOTAL_GET_VALUE.label,
    },
    amount: {
      key: 'amount',
      state: permissions.value.subtotalColumns.amount,
      getValue: SUBTOTAL_GET_VALUE.amount,
    },
    invoicedDate: {
      key: 'invoicedDate',
      state: permissions.value.subtotalColumns.invoicedDate,
      getValue: SUBTOTAL_GET_VALUE.invoicedDate,
    },
    paymentDate: {
      key: 'paymentDate',
      state: permissions.value.subtotalColumns.paymentDate,
      getValue: SUBTOTAL_GET_VALUE.paymentDate,
    },
    comment: {
      key: 'comment',
      state: permissions.value.subtotalColumns.comment,
      getValue: SUBTOTAL_GET_VALUE.comment,
    },
  }));

  const ataColumns = computed<
    Partial<Record<LiftPlanAtaColumnKey, LiftPlanAtaColumn>>
  >(() => ({
    label: {
      key: 'label',
      state: permissions.value.ataColumns.label,
      getValue: ATA_GET_VALUE.label,
    },
    amount: {
      key: 'amount',
      state: permissions.value.ataColumns.amount,
      getValue: ATA_GET_VALUE.amount,
    },
    invoicedDate: {
      key: 'invoicedDate',
      state: permissions.value.ataColumns.invoicedDate,
      getValue: ATA_GET_VALUE.invoicedDate,
    },
    paymentDate: {
      key: 'paymentDate',
      state: permissions.value.ataColumns.paymentDate,
      getValue: ATA_GET_VALUE.paymentDate,
    },
    comment: {
      key: 'comment',
      state: permissions.value.ataColumns.comment,
      getValue: ATA_GET_VALUE.comment,
    },
  }));

  const componentState = computed(
    () =>
      ({
        ...permissions.value.components,
        liftTable: {
          ...permissions.value.components,
          ...permissions.value.columns,
        },
        sectionCard: {
          ...permissions.value.components.sectionCard,
        },
      }) as LiftPlanComponentState,
  );

  // Public action interface

  const action = createEventHandler({
    toggleEdit: () => {
      if (mode.value === 'normal') {
        dispatch({ type: 'toggle-edit' });
        mode.value = 'edit';
      } else if (mode.value === 'edit') {
        dispatch({ type: 'toggle-edit' });
        mode.value = 'saving';
        emit('save', {
          name: storeState.value.liftPlan.name,
          contractSum: storeState.value.liftPlan.contractSum,
          retentionPercentage: storeState.value.liftPlan.retentionPercentage,
          entries: storeState.value.liftPlan.entries.map((e) => ({ ...e })),
          subtotalRows: storeState.value.liftPlan.subtotalRows.map((r) => ({
            ...r,
          })),
          ataRows: storeState.value.liftPlan.ataRows.map((r) => ({ ...r })),
        });
      }
    },

    cancel: () => {
      dispatch({ type: 'cancel' });
      mode.value = 'normal';
    },

    delete: () => {
      if (!window.confirm(t('projects.liftPlanUE.sectionCard.confirmDelete')))
        return;
      emit('delete');
    },

    updateName: (event: { value: string }) =>
      dispatch({ type: 'update-name', value: event.value }),

    updatePlan: (event: {
      field: 'contractSum' | 'retentionPercentage';
      value: string;
    }) =>
      dispatch({ type: 'update-plan', field: event.field, value: event.value }),

    addEntry: () => dispatch({ type: 'add-entry' }),

    deleteEntry: (event: { id: string }) =>
      dispatch({ type: 'delete-entry', id: event.id }),

    updateEntry: (id: string, field: LiftPlanEntryColumnKey, value: string) =>
      dispatch({ type: 'update-entry', id, field, value }),

    addSubtotal: () => dispatch({ type: 'add-subtotal' }),

    deleteSubtotal: (event: { id: string }) =>
      dispatch({ type: 'delete-subtotal', id: event.id }),

    updateSubtotal: (
      id: string,
      field: LiftPlanSubtotalColumnKey,
      value: string,
    ) => dispatch({ type: 'update-subtotal', id, field, value }),

    addAta: () => dispatch({ type: 'add-ata' }),

    deleteAta: (event: { id: string }) =>
      dispatch({ type: 'delete-ata', id: event.id }),

    updateAta: (id: string, field: LiftPlanAtaColumnKey, value: string) =>
      dispatch({ type: 'update-ata', id, field, value }),

    setColumnState: (event: {
      key: LiftPlanEntryColumnKey;
      state: 'visible' | 'hidden';
    }) =>
      dispatch({
        type: 'set-column-state',
        key: event.key,
        state: event.state,
      }),
  });

  const computedState = computed(() => {
    return {
      liftPlan: storeState.value.liftPlan,
      columns: columns.value,
      subtotalColumns: subtotalColumns.value,
      ataColumns: ataColumns.value,
      componentState: componentState.value,
      isSaving: mode.value === 'saving',
      isEditing: storeState.value.isEditing,
    };
  });

  return { state: computedState, action };
};
